import React, { Fragment, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { Button } from 'components/Button';
import { Icon } from 'components/Icon';
import { Modal, ModalFooter, ModalTitle } from 'components/Modal';
import { Tag } from 'components/Tag';
import { H3, H4, HR, Text } from 'components/Typography';
import { theme, toRem } from 'design';
import { Size } from 'design/types';
import { DEFAULT_POOL_NAME, ITipCalculatorResult, ITipCalculatorResultPool, ITipCalculatorResultWarningUnallocatedRemainder, RuleFundSource } from 'types';
import { centsToDollarsDisplay, centsToNegativeDollarsDisplay, toTitleCase } from 'utils';

const SectionWrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  margin: ${toRem(8)} 0;
  font-family: ${theme.font.family.euclid};

  p.subtitle {
    color: ${theme.color.neutral[500]};
    font-size: ${toRem(14)};
    margin: ${toRem(8)} 0;
    text-align: center;
  }
`;

const HeaderWrapper = styled.button`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  margin-right: ${toRem(20)};
  width: 100%;
`;

const TitleItem = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;

  &:first-child {
    width: 100%;
  }

  p {
    color: ${theme.color.neutral[500]};
    font-size: ${toRem(12)};
    font-weight: ${theme.font.weight.medium};
    line-height: ${toRem(20)};
  }
`;

const DropdownIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: ${toRem(32)};
  height: ${toRem(32)};
  margin-right: ${toRem(8)};
  background: ${theme.color.white};
  border: 1px solid ${theme.color.neutral[300]};
  border-radius: 50%;
`;

const DropdownIcon = styled(Icon)`
  &.open {
    transform: rotate(180deg);
  }
`;

const UnallocatedItemLayout = styled.div`
  display: flex;
  padding: ${toRem(12)};
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
  align-self: stretch;
  border-radius: ${toRem(8)};
  border: 1px solid ${theme.color.neutral[200]};
  background: ${theme.color.neutral[50]};
  margin: ${toRem(8)} 0;

  h3 {
    font-size: ${toRem(14)};
    font-weight: ${theme.font.weight.semiBold};
    margin: ${toRem(4)} 0 0 0;
  }
`;

const UnallocatedBreakdown = styled.div`
  display: flex;
  padding: ${toRem(12)};
  flex-direction: column;
  align-items: flex-start;
  gap: ${toRem(12)};
  align-self: stretch;
  border-radius: ${toRem(6)};
  border: 1px solid ${theme.color.neutral[200]};
  background: ${theme.color.white};

  h4 {
    font-size: ${toRem(12)};
    font-weight: 600;
  }

  hr {
    border-top: 1px solid ${theme.color.neutral[200]};
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  width: inherit;
`;

const TableHeaderRow = styled.tr`
  width: inherit;
`;

const TableHeaderItem = styled.th`
  text-align: right;
  color: ${theme.color.neutral[500]};
  font-size: ${toRem(12)};
  font-weight: ${theme.font.weight.medium};
  line-height: ${toRem(18)};
  min-width: ${toRem(80)};
  white-space: nowrap;

  &:first-child {
    text-align: left;
    width: 100%;
  }
`;

const TableBody = styled.tbody`
  width: inherit;
`;

const TableRow = styled.tr`
  width: inherit;
`;

const TableItem = styled.td<{ bold?: boolean }>`
  color: ${theme.color.neutral[900]};
  font-size: ${toRem(12)};
  font-weight: 400;
  line-height: ${toRem(18)};
  min-width: ${toRem(80)};
  text-align: right;

  ${({ bold }) =>
    bold &&
    css`
      font-weight: ${theme.font.weight.semiBold};
    `}

  &:first-child {
    text-align: left;
    width: 100%;
  }
`;

interface IUnallocatedFundsModalProps {
  dataCy?: string;
  warnings: ITipCalculatorResultWarningUnallocatedRemainder[];
  tipCalcResults: ITipCalculatorResult[];
  hideModal?: () => void;
}

interface IDisplayWarnings {
  jobTitle: string;
  results: {
    tipCalcResult: ITipCalculatorResultPool;
    warning: ITipCalculatorResultWarningUnallocatedRemainder;
  }[];
  unallocatedTotal: number;
}

interface IUnallocatedItemProps {
  dataCy: string;
  warning: IDisplayWarnings;
}

const UnallocatedItem: React.FC<IUnallocatedItemProps> = ({ warning, dataCy }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const isDefaultPool = warning.results[0].warning?.segment === DEFAULT_POOL_NAME;

  return (
    <UnallocatedItemLayout data-cy={`${dataCy}-item`}>
      <HeaderWrapper>
        <DropdownIconWrapper data-cy={`${dataCy}-accordion-trigger`} onClick={() => setIsOpen(!isOpen)}>
          <DropdownIcon className={isOpen ? 'open' : ''} icon="Dropdown" fill={theme.color.neutral[500]} />
        </DropdownIconWrapper>
        <TitleWrapper>
          <TitleItem>
            {warning.results[0].warning.isSharedTerminal && !isDefaultPool && <Tag label="Shared POS" size={Size.SMALL} />}
            <H3>{toTitleCase(warning.jobTitle)}</H3>
          </TitleItem>
          <TitleItem>
            <Text>Unallocated</Text>
            <H3>{centsToDollarsDisplay(warning.unallocatedTotal)}</H3>
          </TitleItem>
        </TitleWrapper>
      </HeaderWrapper>
      {isOpen &&
        warning.results.map((result, idx) => (
          <UnallocatedBreakdown data-cy={`${dataCy}-breakdown`} key={`${dataCy}-breakdown-${idx}`}>
            <H4>{toTitleCase(result.warning.segment)}</H4>
            <Table>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderItem>Collected</TableHeaderItem>
                  <TableHeaderItem>Tips</TableHeaderItem>
                  <TableHeaderItem>Autograts</TableHeaderItem>
                  <TableHeaderItem>Total</TableHeaderItem>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableItem>Credit Card</TableItem>
                  <TableItem>{centsToDollarsDisplay(result.tipCalcResult.totals.tip.creditCard)}</TableItem>
                  <TableItem>{centsToDollarsDisplay(result.tipCalcResult.totals.autoGratuity.creditCard)}</TableItem>
                  <TableItem>{centsToDollarsDisplay(result.tipCalcResult.totals.tip.creditCard + result.tipCalcResult.totals.autoGratuity.creditCard)}</TableItem>
                </TableRow>
                <TableRow>
                  <TableItem>Cash</TableItem>
                  <TableItem>{centsToDollarsDisplay(result.tipCalcResult.totals.tip.cash)}</TableItem>
                  <TableItem>{centsToDollarsDisplay(result.tipCalcResult.totals.autoGratuity.cash)}</TableItem>
                  <TableItem>{centsToDollarsDisplay(result.tipCalcResult.totals.tip.cash + result.tipCalcResult.totals.autoGratuity.cash)}</TableItem>
                </TableRow>
                <TableRow>
                  <TableItem>Credit Card Fees</TableItem>
                  <TableItem>{centsToNegativeDollarsDisplay(result.tipCalcResult.totals.tip.creditCardFee)}</TableItem>
                  <TableItem>{centsToNegativeDollarsDisplay(result.tipCalcResult.totals.autoGratuity.creditCardFee)}</TableItem>
                  <TableItem>{centsToNegativeDollarsDisplay(result.tipCalcResult.totals.tip.creditCardFee + result.tipCalcResult.totals.autoGratuity.creditCardFee)}</TableItem>
                </TableRow>
                <TableRow>
                  <TableItem bold>Net</TableItem>
                  <TableItem bold>{centsToDollarsDisplay(result.tipCalcResult.totals.tip.net)}</TableItem>
                  <TableItem bold>{centsToDollarsDisplay(result.tipCalcResult.totals.autoGratuity.net)}</TableItem>
                  <TableItem bold>{centsToDollarsDisplay(result.tipCalcResult.totals.tip.net + result.tipCalcResult.totals.autoGratuity.net)}</TableItem>
                </TableRow>
              </TableBody>
            </Table>
            {result.tipCalcResult.contributions.length > 0 && (
              <>
                <HR />
                <Table>
                  <TableHeader>
                    <TableHeaderRow>
                      <TableHeaderItem>Contributing To</TableHeaderItem>
                      <TableHeaderItem />
                      <TableHeaderItem />
                      <TableHeaderItem />
                    </TableHeaderRow>
                  </TableHeader>
                  <TableBody>
                    {result.tipCalcResult.contributions.map((item, idx) => (
                      <TableRow key={`${dataCy}-contributions-${idx}`}>
                        <TableItem>{item.beneficiaries.map((beneficiary) => toTitleCase(beneficiary.name)).join(' & ')}</TableItem>
                        <TableItem>{centsToNegativeDollarsDisplay(item.sources.find((s) => s.source === RuleFundSource.TIPS)?.total || 0)}</TableItem>
                        <TableItem>{centsToNegativeDollarsDisplay(item.sources.find((s) => s.source === RuleFundSource.AUTO_GRATUITIES)?.total || 0)}</TableItem>
                        <TableItem>{centsToNegativeDollarsDisplay(item.total)}</TableItem>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
            <HR />
            <Table>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderItem>Remainder: Pooled</TableHeaderItem>
                  <TableHeaderItem />
                  <TableHeaderItem />
                  <TableHeaderItem />
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {result.warning.beneficiaries.map((item, idx) => (
                  <Fragment key={`${dataCy}-remainder-${idx}`}>
                    {item.received.map((rec, recIdx) => {
                      const totals = rec.sources.reduce(
                        (acc, source) => {
                          acc.total += Math.round(source.total);

                          if (source.source === RuleFundSource.TIPS) acc.tips += Math.round(source.total);
                          if (source.source === RuleFundSource.AUTO_GRATUITIES) acc.autoGratuities += Math.round(source.total);

                          return acc;
                        },
                        { total: 0, autoGratuities: 0, tips: 0 }
                      );

                      return (
                        <TableRow key={`${dataCy}-remainder-${idx}-${recIdx}`}>
                          <TableItem>{toTitleCase(item.participant.name)}</TableItem>
                          <TableItem>{centsToNegativeDollarsDisplay(totals.tips)}</TableItem>
                          <TableItem>{centsToNegativeDollarsDisplay(totals.autoGratuities)}</TableItem>
                          <TableItem>{centsToNegativeDollarsDisplay(totals.total)}</TableItem>
                        </TableRow>
                      );
                    })}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
            <HR />
            <Table>
              <TableBody>
                <TableRow>
                  <TableItem bold>Unallocated</TableItem>
                  <TableItem bold>{centsToDollarsDisplay(result.warning.remainder.unallocated.tips)}</TableItem>
                  <TableItem bold>{centsToDollarsDisplay(result.warning.remainder.unallocated.autoGratuities)}</TableItem>
                  <TableItem bold>{centsToDollarsDisplay(result.warning.remainder.unallocated.total)}</TableItem>
                </TableRow>
              </TableBody>
            </Table>
          </UnallocatedBreakdown>
        ))}
    </UnallocatedItemLayout>
  );
};

const UnallocatedFundsModal: React.FC<IUnallocatedFundsModalProps> = ({ warnings, tipCalcResults, hideModal, dataCy = 'unallocated-modal' }) => {
  const buildDisplayWarnings = (warnings: ITipCalculatorResultWarningUnallocatedRemainder[], tipCalcResults: ITipCalculatorResult[]) =>
    warnings.reduce<IDisplayWarnings[]>((acc, warning) => {
      const jobTitle = warning.contributor.name;
      const segment = tipCalcResults.find((result) => result.segment.trim().toLowerCase() === warning.segment.toLowerCase());
      const poolResult = segment?.pools.find((pool) => pool.job.title.trim().toLowerCase() === jobTitle.trim().toLowerCase());

      if (!poolResult) return acc;

      const existingDisplayWarning = acc.find((warn) => warn.jobTitle.trim().toLowerCase() === jobTitle.trim().toLowerCase());
      if (existingDisplayWarning) {
        existingDisplayWarning.unallocatedTotal += warning.remainder.unallocated.total;
        existingDisplayWarning.results.push({ tipCalcResult: poolResult, warning });
      } else {
        acc.push({ jobTitle, results: [{ tipCalcResult: poolResult, warning }], unallocatedTotal: warning.remainder.unallocated.total });
      }
      return acc;
    }, []);

  const displayWarnings = useMemo(() => buildDisplayWarnings(warnings, tipCalcResults), [warnings, tipCalcResults]);

  return (
    <Modal maxWidth={640} isActive onClose={hideModal} data-cy={dataCy} hideCloseButton>
      <SectionWrapper>
        <ModalTitle>Unallocated Tips</ModalTitle>
        <Text className="subtitle">We are unable to disburse some of your tips based on the timeclock entries received and your rules. If you need assistance contact Kickfin Support.</Text>
        {displayWarnings.map((warning, idx) => (
          <UnallocatedItem warning={warning} key={`unallocated-item-${idx}`} dataCy={dataCy} />
        ))}
      </SectionWrapper>
      <ModalFooter>
        <Button label="Dismiss" appearance="primary" onClick={hideModal} data-cy="dismiss-button" />
      </ModalFooter>
    </Modal>
  );
};

export default UnallocatedFundsModal;
